import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ADMIN } from '@/constants/testIds';
import { toast } from 'sonner';
import { ChevronLeft, Upload, Star, Trash2, ArrowLeft, ArrowRight } from 'lucide-react';
import imageCompression from 'browser-image-compression';

export default function AnimalEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [animal, setAnimal] = useState(null);
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: a }, { data: s }] = await Promise.all([
        api.get(`/admin/animals/${id}`),
        api.get('/admin/settings'),
      ]);
      setAnimal(a);
      setSettings(s);
    } catch (e) { console.error('Failed to load animal editor:', e); toast.error('Failed to load'); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (!animal) return <div className="text-muted-foreground">Loading…</div>;

  const update = (k, v) => setAnimal((a) => ({ ...a, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: animal.name,
        category: animal.category,
        breed: animal.breed,
        age: animal.age,
        one_liner: animal.one_liner,
        story: animal.story,
        tags: animal.tags,
        display_sponsor_name: animal.display_sponsor_name,
      };
      await api.patch(`/admin/animals/${id}`, payload);
      toast.success('Saved.');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const onFileSelected = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const f of files) {
        const compressed = await imageCompression(f, { maxSizeMB: 1.2, maxWidthOrHeight: 1600, useWebWorker: true });
        const form = new FormData();
        form.append('file', compressed, f.name);
        await api.post(`/admin/animals/${id}/photos`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      toast.success('Photos uploaded');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const moveImage = async (idx, dir) => {
    const gallery = [...(animal.gallery || [])];
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= gallery.length) return;
    [gallery[idx], gallery[swapIdx]] = [gallery[swapIdx], gallery[idx]];
    // adjust cover index if it moved
    let coverIdx = animal.cover_index ?? 0;
    if (coverIdx === idx) coverIdx = swapIdx;
    else if (coverIdx === swapIdx) coverIdx = idx;
    setAnimal({ ...animal, gallery, cover_index: coverIdx });
    try {
      await api.patch(`/admin/animals/${id}`, { gallery, cover_index: coverIdx });
    } catch { toast.error('Reorder failed'); }
  };

  const removeImage = async (idx) => {
    if (!window.confirm('Remove this photo?')) return;
    const gallery = [...(animal.gallery || [])];
    gallery.splice(idx, 1);
    let coverIdx = animal.cover_index ?? 0;
    if (coverIdx === idx) coverIdx = 0;
    else if (coverIdx > idx) coverIdx -= 1;
    setAnimal({ ...animal, gallery, cover_index: coverIdx });
    try {
      await api.patch(`/admin/animals/${id}`, { gallery, cover_index: coverIdx });
      toast.success('Photo removed');
    } catch { toast.error('Failed'); }
  };

  const setCover = async (idx) => {
    setAnimal({ ...animal, cover_index: idx });
    try {
      await api.patch(`/admin/animals/${id}`, { cover_index: idx });
      toast.success('Cover updated');
    } catch { toast.error('Failed'); }
  };

  const toggleTag = (t) => {
    const tags = animal.tags || [];
    const has = tags.includes(t);
    update('tags', has ? tags.filter((x) => x !== t) : [...tags, t]);
  };

  return (
    <div data-testid={ADMIN.animalEditor} className="space-y-6 max-w-4xl">
      <Link to="/admin/animals" className="inline-flex items-center gap-1.5 text-sm link-quiet">
        <ChevronLeft className="h-4 w-4" /> Back to animals
      </Link>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase  text-muted-foreground">{animal.category === 'horse' ? 'Horse' : 'Pony'} · {animal.state}</p>
          <h1 className="font-extrabold text-3xl mt-1">{animal.name}</h1>
        </div>
        <Button data-testid={ADMIN.animalEditorSave} onClick={save} disabled={saving} className="rounded-xl bg-[#1E3A2F]">
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>

      <Card className="p-5 sm:p-6 space-y-4 rounded-2xl border-border">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="grid gap-1.5">
            <Label>Name</Label>
            <Input value={animal.name || ''} onChange={(e) => update('name', e.target.value)} />
          </div>
          <div className="grid gap-1.5">
            <Label>Category</Label>
            <Select value={animal.category} onValueChange={(v) => update('category', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="horse">Horse</SelectItem>
                <SelectItem value="pony">Pony</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5">
            <Label>Breed</Label>
            <Input value={animal.breed || ''} onChange={(e) => update('breed', e.target.value)} placeholder="e.g. Marwari, Thoroughbred" />
          </div>
          <div className="grid gap-1.5">
            <Label>Age</Label>
            <Input value={animal.age || ''} onChange={(e) => update('age', e.target.value)} placeholder="e.g. 12 years" />
          </div>
        </div>
        <div className="grid gap-1.5">
          <Label>One-line personality</Label>
          <Input value={animal.one_liner || ''} onChange={(e) => update('one_liner', e.target.value)} placeholder="A single sentence that captures their character…" />
        </div>
        <div className="grid gap-1.5">
          <Label>Full write-up</Label>
          <Textarea rows={6} value={animal.story || ''} onChange={(e) => update('story', e.target.value)} placeholder="Their story, temperament, favourite things…" />
        </div>
        <div className="grid gap-1.5">
          <Label>Temperament tags</Label>
          <div className="flex flex-wrap gap-2">
            {(settings?.tags || []).map((t) => {
              const active = (animal.tags || []).includes(t);
              return (
                <button key={t} type="button" onClick={() => toggleTag(t)}
                  className={`rounded-full px-3 h-8 text-xs font-medium border transition-colors ${active ? 'bg-[#1E3A2F] text-[hsl(var(--primary-foreground))] border-[#1E3A2F]' : 'bg-transparent border-border text-muted-foreground hover:text-[hsl(var(--foreground))]'}`}
                >{t}</button>
              );
            })}
          </div>
        </div>
        {animal.state === 'sponsored' && (
          <div className="flex items-center gap-3 pt-2 border-t border-border">
            <Switch checked={!!animal.display_sponsor_name} onCheckedChange={(v) => update('display_sponsor_name', v)} />
            <div>
              <p className="text-sm font-medium">Display sponsor name publicly</p>
              <p className="text-xs text-muted-foreground">Shows “Sponsored by {animal.sponsor_name || '—'}” on the public card and detail page.</p>
            </div>
          </div>
        )}
      </Card>

      <Card className="p-5 sm:p-6 rounded-2xl border-border">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <p className="text-xs uppercase  text-muted-foreground">Photo gallery</p>
            <p className="text-sm text-muted-foreground">Up to 6 photos. First image (starred) is the cover.</p>
          </div>
          <label className="inline-flex items-center gap-2 rounded-xl bg-[#1E3A2F] hover:bg-[#1E3A2F]/90 text-[hsl(var(--primary-foreground))] px-4 h-10 text-sm cursor-pointer">
            <Upload className="h-4 w-4" />
            {uploading ? 'Uploading…' : 'Upload photos'}
            <input data-testid={ADMIN.animalPhotoUpload} type="file" accept="image/*" capture="environment" multiple hidden onChange={onFileSelected} disabled={uploading} />
          </label>
        </div>
        <div data-testid={ADMIN.animalGallery} className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {(animal.gallery || []).map((src, i) => (
            <div key={src} className={`relative rounded-xl overflow-hidden border ${animal.cover_index === i ? 'border-[#1E3A2F] ring-2 ring-[hsl(var(--ring))]/40' : 'border-border'}`}>
              <div className="aspect-[4/3]"><img src={src} alt="" className="w-full h-full object-cover" /></div>
              <div className="absolute inset-x-0 bottom-0 bg-black/50 text-white text-xs flex items-center justify-between px-2 py-1">
                <div className="flex gap-1">
                  <button onClick={() => moveImage(i, -1)} className="p-1 hover:bg-white/20 rounded"><ArrowLeft className="h-3 w-3" /></button>
                  <button onClick={() => moveImage(i, 1)} className="p-1 hover:bg-white/20 rounded"><ArrowRight className="h-3 w-3" /></button>
                </div>
                <div className="flex gap-1">
                  <button title="Set as cover" onClick={() => setCover(i)} className={`p-1 rounded ${animal.cover_index === i ? 'text-[#D7BE7A]' : 'hover:bg-white/20'}`}><Star className="h-3 w-3" fill={animal.cover_index === i ? '#D7BE7A' : 'none'} /></button>
                  <button onClick={() => removeImage(i)} className="p-1 hover:bg-white/20 rounded text-red-200"><Trash2 className="h-3 w-3" /></button>
                </div>
              </div>
            </div>
          ))}
          {(animal.gallery || []).length === 0 && (
            <div className="col-span-full text-center py-10 text-muted-foreground text-sm">No photos yet.</div>
          )}
        </div>
      </Card>
    </div>
  );
}
